import { I5WhyAnalisys, IRCAMedia } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { useStyles } from "./DocumentPreviewer.styles";
import { IActionPlanMedia } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import { IIssueMedia } from "entities/RCA/IssueEntity/IIssueEntityInterfaces";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadError, Viewer, Worker } from "@react-pdf-viewer/core";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { pageThumbnailPlugin } from "./pageThumbnailPlugin";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import { PDFWorker } from "constants/constants";
import { useState } from "react";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import { useTranslation } from "react-i18next";
import { removeFileExtension } from "utilities/utilities";

export const DocumentPreviewer = ({
    document,
    handleDelete,
    canDelete,
}: {
    document: IRCAMedia | IActionPlanMedia | IIssueMedia | I5WhyAnalisys;
    handleDelete: () => Promise<void>;
    canDelete: boolean;
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const [isHovered, setIsHovered] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const getFileType = (url: string) => {
        const extension = url.split(".").pop()?.toLowerCase();
        return extension;
    };

    const { name, media_url, media_type } = document.media ?? { name: "", media_url: "", media_type: "" };
    const fileType = getFileType(media_url);
    const thumbnailPluginInstance = thumbnailPlugin();
    const { Cover } = thumbnailPluginInstance;
    const pageThumbnailPluginInstance = pageThumbnailPlugin({
        PageThumbnail: <Cover getPageIndex={() => 0} />,
    });

    const getDocumentColor = (mediaType: string) => {
        return ["csv", "xls", "xlsx"].includes(mediaType)
            ? classes.csvColor
            : ["txt", "doc", "docx"].includes(mediaType)
            ? classes.docColor
            : classes.pdfColor;
    };

    const openModal = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenDeleteModal(true);
    };

    const noPreviewRender = () => (
        <div className={`${classes.previewDocumentWrapper} ${classes.previewDocumentBorder}`}>
            <div className={classes.noPreviewDocumentContainer}>
                <ArticleOutlinedIcon fontSize="large" className={getDocumentColor(media_type)} />
                <span className={`${classes.noPreviewFileType} ${getDocumentColor(media_type)}`}>.{media_type}</span>
                {isHovered && canDelete && <DeleteIcon className={classes.deleteIcon} onClick={openModal} />}
            </div>
        </div>
    );

    const renderPreview = () => {
        switch (fileType) {
            case "pdf":
                return (
                    <Worker workerUrl={PDFWorker}>
                        <div className={`${classes.previewDocumentWrapper} ${classes.previewDocumentBorder}`}>
                            <Viewer
                                fileUrl={media_url}
                                plugins={[pageThumbnailPluginInstance, thumbnailPluginInstance]}
                                renderError={(_: LoadError) => noPreviewRender()}
                            />
                            {isHovered && canDelete && (
                                <DeleteIcon className={classes.deleteIcon} onClick={openModal} />
                            )}
                        </div>
                    </Worker>
                );
            case "txt":
            case "doc":
            case "docx":
            case "csv":
            case "xls":
            case "xlsx":
                return noPreviewRender();
            default:
                return (
                    <div className={`${classes.previewDocumentWrapper} ${classes.previewDocumentBorder}`}>
                        {isHovered && canDelete && <DeleteIcon className={classes.deleteIcon} onClick={openModal} />}
                        <span className={classes.noPreviewFileType}>.{media_type}</span>
                    </div>
                );
        }
    };

    return (
        <div
            onClick={() => {
                window.open(media_url, "_blank");
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={classes.documentWrapper}
        >
            {renderPreview()}
            <p className={classes.documentTitle}>{removeFileExtension(name)}</p>
            <RCAConfirmDeleteModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                onDeleteItem={handleDelete}
                title={t("rca.rca_library.rca_detail.media.delete_modal.title")}
                warning={t("rca.rca_library.rca_detail.media.delete_modal.warning")}
                confirm={t("rca.rca_library.rca_detail.media.delete_modal.confirm")}
            />
        </div>
    );
};
